import { Component, ElementRef, inject, OnInit, input } from '@angular/core';
import {
    UntypedFormBuilder,
    Validators,
    FormsModule,
    ReactiveFormsModule,
} from '@angular/forms';
import {
    EditableItemSectionComponent,
    ContextFrameComponent,
    ActionListItemComponent,
    ExpandableComponent,
    DictionaryComponent,
    DictionaryItemComponent,
} from '@exl-ng/mulo-common';
import { EntityService } from '../../../core/entity.service';
import { EntityLocalFields } from '../../../shared/interfaces/entitiy-local-fields.interface';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MatList } from '@angular/material/list';
import { MatButton } from '@angular/material/button';
import { MatInput } from '@angular/material/input';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'esp-entity-local-fields',
    templateUrl: './entity-local-fields.component.html',
    styleUrls: ['./entity-local-fields.component.scss'],
    imports: [
        ContextFrameComponent,
        FormsModule,
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatSelect,
        MatOption,
        MatInput,
        MatError,
        MatButton,
        MatList,
        ActionListItemComponent,
        ExpandableComponent,
        DictionaryComponent,
        DictionaryItemComponent,
        TranslateModule,
        MatIcon,
        MatIcon,
    ],
})
export class EntityLocalFieldsComponent
    extends EditableItemSectionComponent<EntityLocalFields>
    implements OnInit
{
    host = inject(ElementRef);
    private formBuilder = inject(UntypedFormBuilder);
    private translate = inject(TranslateService);

    readonly parentId = input<string>(undefined);

    readonly localFieldNames = input<string[]>(undefined);
    readonly requiredFields = input<string[]>([]);
    readonly showRemoveBtn = input(true);
    readonly uniqueCodes = input(false);
    readonly entityService = input<EntityService>(inject(EntityService));

    constructor() {
        super();
    }

    ngOnInit(): void {
        this.form = this.formBuilder.group({
            code: ['', Validators.required],
            localFieldValue: ['', Validators.required],
        });
    }

    handleSubmit(andClose = true) {
        this.submitInProgress.set(true);
        const existing = this.items().find(
            (item) =>
                item.code === this.form.get('code').value &&
                item.localFieldValue === this.form.get('localFieldValue').value,
        );

        this.closeForm = andClose;
        if (!existing) {
            if (this.formMode() === 'add') {
                this.addField();
            } else {
                this.editField();
            }
        } else {
            console.error(`${existing.value} exists`);
            super.handleFormClose();
        }
    }

    codeExists = (code: string) =>
        this.items()?.some(
            (item) =>
                code === item.code &&
                code !== this.items()[this.itemInEdit()]?.code,
        );

    addField() {
        const localField: EntityLocalFields = {
            code: this.form.get('code').value,
            localFieldValue: this.form.get('localFieldValue').value,
            value: this.form.get('localFieldValue').value,
            parentId: this.parentId(),
        };

        this.entityService()
            .updateSectionItem('localFields', localField)
            .subscribe((item: EntityLocalFields) => {
                super.addItem(item);
            });
    }

    editField() {
        const index = this.itemInEdit();
        const localField: EntityLocalFields = {
            ...this.items()[index],
            code: this.form.get('code').value,
            localFieldValue: this.form.get('localFieldValue').value,
            value: this.form.get('localFieldValue').value,
            parentId: this.parentId(),
        };

        this.entityService()
            .updateSectionItem('localFields', localField)
            .subscribe((item: EntityLocalFields) => {
                super.editItem(item, this.itemInEdit());
            });
    }

    removeField(index) {
        const item = this.items()[index];
        this.entityService()
            .deleteSectionItem(
                'localFields',
                item.id,
                () => super.removeItem(index),
                this.ariaLabel(),
                this.translate.instant(item.code),
            )
            .subscribe();
    }
}
